import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Diamond } from "lucide-react";
import { useLang } from "../i18n";
import { scrollToId } from "../smooth";
import { MaskLine } from "./Reveal";

const Hero = () => {
    const { t } = useLang();
    const { scrollY } = useScroll();
    const yBg = useTransform(scrollY, [0, 900], [0, 130]);
    const yText = useTransform(scrollY, [0, 900], [0, 60]);

    return (
        <section id="top" data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden bg-navy">
            <motion.div
                className="absolute inset-0 bg-cover bg-[52%_center] md:bg-[right_center]"
                style={{
                    backgroundImage: "url(/assets/hero-bg.jpg)",
                    y: yBg,
                    scale: 1.12,
                }}
                initial={{ scale: 1.22, opacity: 0.6 }}
                animate={{ scale: 1.12, opacity: 1 }}
                transition={{ duration: 2.6, ease: [0.16, 1, 0.3, 1] }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-ivory/90 via-ivory/60 to-ivory/35 md:bg-gradient-to-r md:from-ivory/40 md:via-transparent md:to-transparent" />

            <motion.div
                style={{ y: yText }}
                className="relative z-10 mx-auto flex min-h-[100svh] max-w-7xl flex-col justify-center px-6 pt-28 pb-16 md:px-12 lg:max-w-none lg:pl-[7vw]"
            >
                <MaskLine delay={0.35}>
                    <span className="text-[10px] tracking-[0.5em] text-champagne uppercase md:text-xs">
                        {t.hero.eyebrow}
                    </span>
                </MaskLine>

                <h1 className="mt-6 font-serif text-[44px] leading-[1.04] text-ink sm:text-6xl lg:text-[72px]" data-testid="hero-headline">
                    <MaskLine delay={0.5}>{t.hero.title1}</MaskLine>
                    <MaskLine delay={0.62}>{t.hero.title2}</MaskLine>
                    <MaskLine delay={0.74}>{t.hero.title3}</MaskLine>
                </h1>

                <motion.div
                    initial={{ opacity: 0, scaleX: 0 }}
                    animate={{ opacity: 1, scaleX: 1 }}
                    transition={{ duration: 1.1, delay: 1.0, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-7 flex origin-left items-center gap-3"
                >
                    <span className="h-px w-16 bg-champagne" />
                    <span className="h-1.5 w-1.5 rotate-45 bg-champagne" />
                    <span className="h-px w-16 bg-champagne" />
                </motion.div>

                <MaskLine delay={1.05} className="mt-7">
                    <span className="text-[10px] tracking-[0.42em] text-ink/80 md:text-xs">
                        {t.hero.collection}
                    </span>
                </MaskLine>

                <MaskLine delay={1.2} className="mt-6">
                    <span className="font-serif text-base italic leading-relaxed text-ink/90 md:text-xl">
                        {t.hero.desc1}
                        <br />
                        {t.hero.desc2}
                        <br />
                        {t.hero.desc3}
                    </span>
                </MaskLine>

                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 1.45, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-9"
                >
                    <a
                        href="#collection"
                        data-testid="hero-cta"
                        onClick={(e) => {
                            e.preventDefault();
                            scrollToId("#collection");
                        }}
                        className="group inline-flex items-center gap-3 bg-navy px-7 py-3.5 text-[11px] tracking-[0.28em] text-ivory uppercase transition-colors duration-500 hover:bg-navy-deep"
                    >
                        {t.hero.cta}
                        <ArrowRight
                            size={14}
                            strokeWidth={1.6}
                            className="text-champagne transition-transform duration-500 group-hover:translate-x-1.5"
                        />
                    </a>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.2, delay: 1.7 }}
                    className="mt-9 flex items-center gap-3"
                    data-testid="hero-badge"
                >
                    <Diamond size={13} strokeWidth={1.4} className="text-champagne" />
                    <span className="text-[10px] tracking-[0.34em] text-ink/75 md:text-[11px]">
                        {t.hero.badge}
                    </span>
                </motion.div>
            </motion.div>
        </section>
    );
};

export default Hero;
